import { logger } from '@enterprise/common';

export function isNotNullOrUndefined<T>(v: T | null | undefined): v is T {
  return v !== null && typeof v !== 'undefined';
}

export function isUndefined(v: any): v is undefined {
  return typeof v === 'undefined';
}

export function isBoolean(v: any): v is boolean {
  return typeof v === 'boolean';
}

export function isNull(v: any): v is null {
  return v === null;
}

export function isFunction(v: any): v is Function {
  return typeof v === 'function';
}

export function isNumber(v: any): v is number {
  return typeof v === 'number';
}

export function isString(v: any): v is string {
  return typeof v === 'string';
}

export function isObject(v: any): v is object {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function isUid(prefix: string, v: unknown): boolean {
  return isString(v) && v.startsWith(prefix) && v.length === 36 + prefix.length;
}

export function isEmpty(v: any): boolean {
  // If v is an array destructure it and reconstitute it as an object
  if (Array.isArray(v)) {
    return isEmpty(
      v.reduce((acc, cur, i) => {
        acc[i] = cur;
        return acc;
      }, {})
    );
  }

  // If v is not an object check if it's empty (undefined,null, or empty string)
  if (!isObject(v)) {
    return !isNotNullOrUndefined(v) || v === '';
  }

  // If it is an object, check its properties...
  const isEmptyObject = Object.entries(v ?? {}).every(([, value]) => {
    // Null or undefined
    if (isUndefined(value) || isNull(value)) {
      return true;
    }

    // Empty string
    else if (isString(value)) {
      return value === '';
    }

    // Object or Array, check recursively
    else if (isObject(value) || Array.isArray(value)) {
      return isEmpty(value);
    }

    // Otherwise, it's not empty
    else return false;
  });

  return isEmptyObject;
}

export function assertString(v: unknown): asserts v is string {
  if (typeof v !== 'string') throw new Error(`${v} is not a string`);
}

export function assertNumber(v: unknown): asserts v is number {
  if (typeof v !== 'number' || Number.isNaN(v)) throw new Error(`${v} is not a number or is NaN`);
}

/**
 * Should throw an error if switch block is not exhaustive
 * E.g. type myCase = 'A' | 'B' | 'C'
 * switch (myCase) {
 *     case 'A':
 *       return 'somethingA';
 *    case 'B':
 *       return 'somethingB';
 *     default:
 *       exhaustiveCheck(myCase); <-- should throw an error because myCase 'C' is not assignable to parameter of type 'never'
 *       return undefined;
 *   }
 * @param {never} v
 */
export function assertUnreachable(v: never): void {
  logger.warn(`Unhandled switch case: ${v}`);
}
